import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { COMPETENCY_LABELS, type CompetencyLevel } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import { Card, EmptyState, Spinner } from '../../components/ui';
import { api } from '../../lib/api';

interface Matrix {
  steps: Array<{ id: string; stepNo: number; name: string; inPilot: boolean }>;
  operators: Array<{
    id: string;
    fullName: string;
    shift: string | null;
    steps: Array<{ stepNo: number; inPilot: boolean; level: CompetencyLevel }>;
    oplsAssigned: number;
    oplsCompetent: number;
  }>;
}

/** Operator skill matrix (directive §31). */
export function SkillMatrixPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['skill-matrix'],
    queryFn: () => api.get<Matrix>('/api/training/matrix'),
  });

  if (isLoading || !data) return <Spinner label="Loading skill matrix" />;

  return (
    <div>
      <PageHeader
        title="Operator skill matrix"
        subtitle="AM step competency across the pilot operators"
      />

      <div className="space-y-4 p-4 lg:p-6">
        {data.operators.length === 0 ? (
          <Card>
            <EmptyState
              icon={Users}
              title="No operators yet"
              message="Add operators to the pilot and their AM step progress will appear here."
            />
          </Card>
        ) : (
          <Card className="overflow-hidden">
            {/* Wide table scrolls inside its own container — the page never does. */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-sm">
                <caption className="sr-only">
                  Operator competency by Autonomous Maintenance step
                </caption>
                <thead>
                  <tr className="border-b border-steel-200 bg-steel-50">
                    <th scope="col" className="px-4 py-2 text-left font-bold text-steel-700">
                      Operator
                    </th>
                    {data.steps.map((step) => (
                      <th
                        key={step.id}
                        scope="col"
                        className={`px-3 py-2 text-center font-bold ${
                          step.inPilot ? 'text-steel-700' : 'text-steel-400'
                        }`}
                        title={step.name}
                      >
                        Step {step.stepNo}
                        {!step.inPilot ? (
                          <span className="block text-[10px] font-normal">not in pilot</span>
                        ) : null}
                      </th>
                    ))}
                    <th scope="col" className="px-3 py-2 text-center font-bold text-steel-700">
                      OPLs
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.operators.map((operator) => (
                    <tr
                      key={operator.id}
                      className="cursor-pointer border-b border-steel-100 hover:bg-steel-50"
                      onClick={() => navigate(`/training/${operator.id}`)}
                    >
                      <th scope="row" className="px-4 py-3 text-left font-semibold text-steel-900">
                        {operator.fullName}
                        {operator.shift ? (
                          <span className="block text-xs font-normal text-steel-500">
                            Shift {operator.shift}
                          </span>
                        ) : null}
                      </th>
                      {operator.steps.map((step) => (
                        <td key={step.stepNo} className="px-3 py-3 text-center">
                          <LevelMark level={step.level} inPilot={step.inPilot} />
                        </td>
                      ))}
                      <td className="px-3 py-3 text-center tabular-nums">
                        <span className="font-bold text-steel-900">{operator.oplsCompetent}</span>
                        <span className="text-steel-400"> / {operator.oplsAssigned}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap gap-4 border-t border-steel-200 bg-steel-50 px-4 py-3 text-xs text-steel-600">
              {(['NONE', 'AWARE', 'ASSISTED', 'INDEPENDENT', 'CAN_TEACH'] as CompetencyLevel[]).map(
                (level) => (
                  <span key={level} className="flex items-center gap-1.5">
                    <LevelMark level={level} inPilot />
                    {COMPETENCY_LABELS[level]}
                  </span>
                ),
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

/**
 * Competency mark.
 * Shape + colour + a screen-reader label: never colour alone (directive §63).
 */
function LevelMark({ level, inPilot }: { level: CompetencyLevel; inPilot: boolean }) {
  const config: Record<CompetencyLevel, { symbol: string; className: string }> = {
    NONE: { symbol: '○', className: 'text-steel-300' },
    AWARE: { symbol: '◔', className: 'text-info-500' },
    ASSISTED: { symbol: '◑', className: 'text-warn-500' },
    INDEPENDENT: { symbol: '●', className: 'text-ok-600' },
    CAN_TEACH: { symbol: '★', className: 'text-ok-700' },
  };
  const { symbol, className } = config[level];

  return (
    <span className={inPilot ? className : 'text-steel-200'}>
      <span aria-hidden className="text-lg leading-none">
        {symbol}
      </span>
      <span className="sr-only">{COMPETENCY_LABELS[level]}</span>
    </span>
  );
}
